/* eslint-disable react/display-name */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/jsx-boolean-value */
import React, { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Paper,
  Button,
} from '@material-ui/core';
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableColumnDef,
} from 'mui-datatables';
import { Alert } from '@material-ui/lab';
import Loading from 'components/Loading';
import { Toast } from 'components/Toast';
import {
  COMPLIANCE_BROKEN_LINKS,
  SCANNER_BROKEN_LINKS,
} from 'queries/compliance';
import { CHECK_SITE_HEALTH } from 'queries/site';

interface IBrokenLinks {
  url: string;
  postURL: string;
  status: number;
}

interface IProps {
  data: {
    siteId: string;
    domain: string;
  };
}

const BrokenLinks: React.FC<IProps> = ({ data: { siteId, domain } }) => {
  const [brokenLinks, setBrokenLinks] = useState<IBrokenLinks[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [load, { data, loading }] = useLazyQuery(COMPLIANCE_BROKEN_LINKS, {
    variables: {
      domain,
      page: currentPage + 1,
      limit: rowsPerPage,
    },
    fetchPolicy: 'no-cache',
  });

  const [mutation] = useMutation(SCANNER_BROKEN_LINKS, {
    variables: { domain, siteId },
    onCompleted: () =>
      Toast(
        'Search started successfully! When the process is finished, you will receive a notification in the Google Chat Space.',
        'success',
      ),
    onError: () => Toast('Error searching for broken links!', 'error'),
  });

  useEffect(() => {
    load();
  }, [load, currentPage, rowsPerPage]);

  useEffect(() => {
    if (data?.complianceBrokenLinks) {
      setBrokenLinks(data.complianceBrokenLinks.urls);
      setTotal(data.complianceBrokenLinks.total);
    }
  }, [data]);

  const handleSearchBrokenLinks = () => mutation();

  const handleTableChange = (action: string, tableState: any) => {
    if (action === 'changePage') {
      setCurrentPage(tableState.page);
    }
    if (action === 'changeRowsPerPage') {
      setRowsPerPage(tableState.rowsPerPage);
      setCurrentPage(0);
    }
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'url',
      label: 'Broken Link',
      options: {
        customBodyRender: (value: string) => (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        ),
      },
    },
    {
      name: 'postURL',
      label: 'Page where found',
      options: {
        customBodyRender: (value: string) => (
          <a
            href={`https://${value}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Page
          </a>
        ),
      },
    },
    {
      name: 'status',
      label: 'Server Response',
    },
  ];

  const options: MUIDataTableOptions = {
    serverSide: true,
    count: total,
    page: currentPage,
    rowsPerPage,
    selectableRows: 'none',
    responsive: 'standard',
    onTableChange: handleTableChange,
  };

  const [loadCheckSiteHealth, { data: siteHealthData }] = useLazyQuery(
    CHECK_SITE_HEALTH,
    {
      variables: { domain: `https://${domain}` },
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    let mounted = true;
    if (mounted) loadCheckSiteHealth();
    return () => {
      mounted = false;
    };
  }, [loadCheckSiteHealth]);

  const isSiteOnline =
    siteHealthData && siteHealthData.checkSiteHealth.isSiteOnline;

  return (
    <Paper style={{ padding: 12 }}>
      <Grid item xs={12}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isSiteOnline ? (
            <Alert severity="success">
              <strong>Attention:</strong> {domain} is online
            </Alert>
          ) : (
            <Alert severity="warning">
              <strong>Attention:</strong> {domain} is offline
            </Alert>
          )}
        </div>
      </Grid>

      <Accordion style={{ marginTop: 12 }}>
        <AccordionSummary
          aria-controls="panel1-content"
          id="panel1-header"
          style={{ backgroundColor: '#F6F6F6' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <div>
              <i className="fa fa-link" style={{ marginRight: '4px' }} />
              Broken Links
            </div>
            <div>
              {total === 0 ? (
                <span style={{ color: 'green' }}>No broken links found</span>
              ) : (
                <span style={{ color: 'orange' }}>
                  {total} broken link(s) found
                </span>
              )}
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
          >
            {loading ? (
              <Loading openLoading={true} />
            ) : total === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSearchBrokenLinks}
                >
                  Search by Broken Links
                </Button>
              </div>
            ) : (
              <div style={{ width: '100%' }}>
                <MUIDataTable
                  title={false}
                  data={brokenLinks}
                  columns={columns}
                  options={options}
                />
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default BrokenLinks;
